import { memo } from 'react';
import { MonitorUp } from 'lucide-react';
import { Button, Modal } from '@cyphex/ui';
import type { SharePresenter } from '../media/shareArbiter';

export interface TakeOverDialogProps {
  /** The presenter standing in the way, or null when the dialog is closed. */
  presenter: SharePresenter | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * "Ganesh is presenting. Take over?"
 *
 * The refusal this answers happens BEFORE any capture: the browser's chooser is
 * never opened just to have its answer discarded, and on the desktop client the
 * native source picker is not opened either. So by the time this dialog is on
 * screen nothing has been captured and there is no share to leak if the user
 * says no.
 *
 * Saying yes stops the other person's share. It is worded as taking something,
 * because that is what it does — the stage has one slot, and a "share anyway"
 * that quietly published a second stream nobody could see is exactly the
 * behaviour this replaces.
 */
export const TakeOverDialog = memo(function TakeOverDialog(props: TakeOverDialogProps) {
  const { presenter } = props;

  return (
    <Modal
      open={presenter !== null}
      onOpenChange={(open) => {
        if (!open) props.onCancel();
      }}
      title={`${presenter?.name ?? 'Someone'} is presenting`}
      description="Only one person can present at a time."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={props.onCancel}>
            Cancel
          </Button>
          <Button variant="primary" leftIcon={MonitorUp} onClick={props.onConfirm}>
            Take over
          </Button>
        </>
      }
    >
      <p className="text-base text-muted-foreground">
        Taking over stops {presenter?.name ?? 'their'} screen share and puts yours on the stage.
        They will be told what happened.
      </p>
    </Modal>
  );
});
